/**
 * Iter 76 — useFieldConfig(formId) hook.
 *
 * Fetches the field configuration for a given form once and returns
 * helpers any form can drop in:
 *
 *   const fc = useFieldConfig('employee_master');
 *   <input required={fc.isRequired('email', true)}
 *          hidden={fc.isHidden('phone')}
 *          placeholder={fc.label('email', 'Email Address')} />
 *
 * Falls back to caller-provided defaults if the field isn't in the
 * config (e.g. brand-new field not yet in the registry).
 */
import { useEffect, useState, useMemo } from 'react';
import { apiGet } from '../lib/api';

const _cache = new Map();

export function useFieldConfig(formId) {
  const [fields, setFields] = useState(_cache.get(formId) || null);

  useEffect(() => {
    if (!formId) return;
    if (_cache.has(formId)) {
      setFields(_cache.get(formId));
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await apiGet(`/form-config/${formId}`);
        const map = {};
        for (const f of (r.fields || [])) map[f.id] = f;
        _cache.set(formId, map);
        if (alive) setFields(map);
      } catch (e) {
        // Silently fall back to defaults; the form still works
        if (alive) setFields({});
      }
    })();
    return () => { alive = false; };
  }, [formId]);

  return useMemo(() => ({
    loading: fields === null,
    isRequired: (fieldId, defaultVal = false) => {
      const f = fields?.[fieldId];
      return f?.is_required !== undefined ? !!f.is_required : defaultVal;
    },
    isHidden: (fieldId, defaultVal = false) => {
      const f = fields?.[fieldId];
      return f?.is_hidden !== undefined ? !!f.is_hidden : defaultVal;
    },
    label: (fieldId, defaultVal = '') => {
      const f = fields?.[fieldId];
      return f?.label || defaultVal;
    },
    help: (fieldId) => fields?.[fieldId]?.help_text || null,
  }), [fields]);
}

/** Reset cache (called after the admin saves a form config). */
export function clearFieldConfigCache(formId) {
  if (formId) _cache.delete(formId);
  else _cache.clear();
}
